import { request } from './client'

/* ============================================================================
   W-136 — THE CARDS IN THE ROOMS.

   Issue, revoke and reprint the code on every door. **APPEND-ONLY**: revoking a code
   ends it and issuing writes a new row, so a request received six weeks ago stays
   attributable to the card that was actually on the door at the time — which is the only
   way a complaint from three weeks ago can be looked into at all.

   THE TOKEN COMES DOWN IN CLEAR, and that is a decision rather than an oversight: it is
   printed on a card in a bedroom, so it is not a secret from anybody who walks into the
   room, and the person managing the codes is exactly the person who has to reprint one
   after a card is torn.
   ========================================================================= */

export interface QrCodeView {
  qrCodeId: string
  roomId: string
  roomNumber: string
  /** The floor's code — the screen groups by it, because that is how somebody walks the
      building with a stack of cards. */
  floor: string
  token: string
  /** The token as it is printed — four groups of four. For the eye only. */
  printedToken: string
  issuedAtUtc: string
  revokedAtUtc: string | null
  revokeReason: string | null
  /** How many requests came through this code. Forty against one room in an afternoon is
      the shape of a card being misused. */
  requestCount: number
}

/** "Are we ready to print?" is a question about the SET, not about any one room. */
export interface QrCodeSummary {
  rooms: number
  withLiveCode: number
  withoutCode: number
  revoked: number
}

/**
 * A ROOM WITH NO CARD IN IT — deliberately not a `QrCodeView` with the token blanked.
 *
 * It has no code, no issue date and no request count. A row with empty ids carrying it
 * would put a thing on the wire that looks like a code and is not.
 */
export interface RoomWithoutCode {
  roomId: string
  roomNumber: string
  floor: string
}

export interface QrCodeBoard {
  summary: QrCodeSummary
  codes: QrCodeView[]
  /** THE ROOMS ARE THE DENOMINATOR — the rooms with no card are precisely the rows a
      list built from the CODES would not contain, and they are the point of the screen. */
  roomsWithoutCode: RoomWithoutCode[]
}

export const fetchQrCodes = (
  propertyId: string, includeRevoked: boolean, signal?: AbortSignal,
) => request<QrCodeBoard>(
  `/properties/${propertyId}/qr-codes?includeRevoked=${includeRevoked ? 'true' : 'false'}`,
  signal ? { signal } : {})

export const issueQrCode = (propertyId: string, roomId: string) =>
  request<QrCodeView>(`/properties/${propertyId}/rooms/${roomId}/qr-code`, { method: 'POST' })

/** A code for every room that has not got one. Idempotent by construction. */
export const issueMissingQrCodes = (propertyId: string) =>
  request<{ issued: number }>(`/properties/${propertyId}/qr-codes/issue-missing`, { method: 'POST' })

/**
 * A REVOCATION, NOT A DELETE — and it always carries a reason.
 *
 * The reason is the difference between "this card was abused" and "we renumbered the
 * floor", six weeks later when somebody asks why room 305 has three dead codes. The
 * server refuses a blank one; the screen shuts the button, which is the courtesy.
 */
export const revokeQrCode = (qrCodeId: string, reason: string) =>
  request<void>(`/qr-codes/${qrCodeId}/revocation`, { method: 'POST', body: { reason } })

/**
 * THE SHEET OF CARDS, AS A PDF.
 *
 * A blob, not a link: the download needs the `Authorization` header and a browser sends
 * none on a plain navigation. Read as a BLOB — read as text, a PDF is silently corrupted.
 * The same path W-110's report download takes, for the same reasons.
 */
export const fetchQrSheetPdf = (propertyId: string) =>
  request<Blob>(`/properties/${propertyId}/qr-codes/sheet?format=pdf`, { expect: 'blob' })
